import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { CheckIcon, ArrowRightIcon } from 'lucide-react';
const steps = [{
  num: '01',
  title: 'A private, no-pressure consultation',
  body: 'We sit down — usually with a coffee — and talk about her, the moment, and the budget. No tray of rings pushed across the counter.'
}, {
  num: '02',
  title: 'Diamonds chosen to your brief, not ours',
  body: "We show you three stones within your budget. We tell you honestly what's worth paying more for, and what isn't."
}, {
  num: '03',
  title: 'A design built around your story',
  body: "Sketches first. Then CAD. Then a wearable 3D resin proof — so you see and feel the ring before it's made."
}, {
  num: '04',
  title: 'Handcrafted on our Ardross bench',
  body: "By our fully trained Sinclair's jewellers — assembled and finished by hand, in-house."
}, {
  num: '05',
  title: 'Quality-checked, presented, protected',
  body: 'Final polish, certificate, and proposal-ready box. Lifetime inspections and cleaning included for as long as you wear it.'
}];
export function EngagementRings() {
  const [activeStep, setActiveStep] = useState(0);
  return <section id="engagement-rings" className="section-padding bg-background" data-id="element-150">
      <div className="container-custom" data-id="element-151">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16" data-id="element-152">
          {/* Image column */}
          <motion.div initial={{
          opacity: 0,
          scale: 0.97
        }} whileInView={{
          opacity: 1,
          scale: 1
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 1
        }} className="lg:col-span-5 lg:sticky lg:top-28 lg:self-start" data-id="element-153">
            <div className="relative" data-id="element-154">
              <div className="relative aspect-square overflow-hidden bg-paper" data-id="element-155">
                <img src="https://cdn.magicpatterns.com/uploads/w2f7n1Ys2Wayy5BDiaHQfc/d1-2.webp" alt="A custom marquise diamond engagement ring with pavé band in 18ct yellow gold, handcrafted by Sinclairs Jewellers in Ardross, Perth" className="absolute inset-0 w-full h-full object-contain p-10" data-id="element-156" />
              </div>
              {/* Tag overlay */}
              <div className="absolute top-5 left-5 bg-background px-4 py-2.5 shadow-md" data-id="element-157">
                <p className="brand-mark text-[0.6rem] text-gold-dark uppercase" data-id="element-158">
                  A Sinclairs Custom Ring
                </p>
              </div>
              {/* Live step indicator driven by the active stepper selection */}
              <div className="absolute bottom-5 left-5 right-5 bg-primary/95 backdrop-blur-sm px-5 py-4" data-id="element-159">
                <div className="flex items-center gap-3" data-id="element-160">
                  <span className="signature text-3xl text-gold-light leading-none shrink-0" data-id="element-161">
                    {steps[activeStep].num}
                  </span>
                  <AnimatePresence mode="wait" data-id="element-162">
                    <motion.p key={activeStep} initial={{
                    opacity: 0,
                    y: 6
                  }} animate={{
                    opacity: 1,
                    y: 0
                  }} exit={{
                    opacity: 0,
                    y: -6
                  }} transition={{
                    duration: 0.25
                  }} className="font-serif text-sm text-white leading-snug" data-id="element-163">
                      {steps[activeStep].title}
                    </motion.p>
                  </AnimatePresence>
                </div>
                {/* Progress track */}
                <div className="mt-3 flex gap-1.5" data-id="element-164">
                  {steps.map((_, i) => <span key={i} className={`h-1 flex-1 transition-colors duration-300 ${i <= activeStep ? 'bg-gold-light' : 'bg-white/20'}`} data-id="element-165" />)}
                </div>
              </div>
            </div>

            {/* Price reassurance card */}
            <div className="mt-7 bg-paper border-l-2 border-gold p-6 md:p-7" data-id="element-166">
              <p className="brand-mark text-[0.65rem] text-gold-dark uppercase mb-3" data-id="element-167">
                Price reassurance
              </p>
              <p className="font-serif text-2xl text-primary mb-2 leading-tight" data-id="element-168">
                Custom engagement rings from{' '}
                <span className="text-gold-dark" data-id="element-169">$3,500</span>
              </p>
              <p className="text-sm text-secondary leading-relaxed" data-id="element-170">
                Most of our clients spend between $5,000 and $15,000. You'll
                always get a firm written quote before any work begins — and
                we'll tell you honestly if your idea can be made for less.
              </p>
              <p className="text-[0.65rem] text-muted italic mt-3" data-id="element-171">
                [VERIFY] · Confirm pricing bands with client
              </p>
            </div>
          </motion.div>

          {/* Steps column */}
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true,
          margin: '-100px'
        }} transition={{
          duration: 0.9
        }} className="lg:col-span-7" data-id="element-172">
            <span className="eyebrow" data-id="element-173">Custom Engagement Rings</span>
            <h2 className="font-serif text-3xl md:text-4xl lg:text-[2.75rem] font-medium leading-[1.1] mb-7 text-balance" data-id="element-174">
              A ring designed around{' '}
              <span className="italic text-gold-dark" data-id="element-175">her</span> — not around
              what we have in the cabinet.
            </h2>
            <p className="text-secondary text-lg mb-10 max-w-xl text-pretty" data-id="element-176">
              You can come in with a Pinterest board, a single sketch, a family
              stone you want to use — or no idea at all. We'll handle the rest.
            </p>

            {/*
             Interactive stepper.
             IMPORTANT: every step's title AND body stays rendered in the DOM
             at all times for crawlability — selecting a step only changes
             emphasis, it never unmounts non-active step content.
             */}
            <ol className="space-y-px bg-gold/15 border border-gold/15 mb-10" aria-label="How our custom engagement ring process works" data-id="element-177">
              {steps.map((step, index) => {
              const isActive = index === activeStep;
              return <li key={index} className="relative bg-background" data-id="element-178">
                    <button type="button" onClick={() => setActiveStep(index)} aria-expanded={isActive} aria-current={isActive ? 'step' : undefined} className={`group w-full text-left flex gap-5 md:gap-7 p-6 md:p-7 transition-colors duration-300 ${isActive ? 'bg-paper' : 'hover:bg-paper/60'}`} data-id="element-179">
                      {isActive && <motion.span layoutId="er-active-accent" className="absolute left-0 top-0 bottom-0 w-0.5 bg-gold-dark" transition={{
                    type: 'spring',
                    stiffness: 400,
                    damping: 34
                  }} data-id="element-180" />}
                      <span className={`signature text-5xl md:text-6xl leading-none shrink-0 w-14 md:w-16 transition-colors duration-300 ${isActive ? 'text-gold-dark' : 'text-gold/45 group-hover:text-gold-dark'}`} data-id="element-181">
                        {step.num}
                      </span>
                      <div data-id="element-182">
                        <h3 className={`font-serif text-lg md:text-xl mb-1.5 leading-snug transition-colors duration-300 ${isActive ? 'text-primary' : 'text-secondary group-hover:text-primary'}`} data-id="element-183">
                          {step.title}
                        </h3>
                        {/*
                         Body is ALWAYS in the DOM. On active step it is fully
                         emphasised; otherwise it is dimmed but never removed.
                         */}
                        <p className={`text-sm md:text-base leading-relaxed text-pretty transition-colors duration-300 ${isActive ? 'text-secondary' : 'text-muted'}`} data-id="element-184">
                          {step.body}
                        </p>
                      </div>
                    </button>
                  </li>;
            })}
            </ol>

            {/* No-surprises promise */}
            <div className="bg-primary text-white p-7 md:p-9 mb-8 border-l-2 border-gold" data-id="element-185">
              <p className="eyebrow-light" data-id="element-186">Our written promise</p>
              <p className="font-serif text-xl md:text-2xl text-white leading-snug mb-3" data-id="element-187">
                You'll never see a price you weren't expecting.
              </p>
              <ul className="space-y-2 text-sm text-white/80" data-id="element-188">
                <li className="flex items-start gap-2" data-id="element-189">
                  <CheckIcon className="w-4 h-4 text-gold-light shrink-0 mt-0.5" data-id="element-190" />{' '}
                  Firm written quote before any work begins
                </li>
                <li className="flex items-start gap-2" data-id="element-191">
                  <CheckIcon className="w-4 h-4 text-gold-light shrink-0 mt-0.5" data-id="element-192" />{' '}
                  Your design approval required before manufacture
                </li>
                <li className="flex items-start gap-2" data-id="element-193">
                  <CheckIcon className="w-4 h-4 text-gold-light shrink-0 mt-0.5" data-id="element-194" />{' '}
                  Lifetime cleaning &amp; inspection — included, always
                </li>
              </ul>
            </div>

            <a href="#consultation" className="btn-gold group" data-id="element-195">
              Start your engagement ring consultation
              <ArrowRightIcon className="w-4 h-4 ml-2 transition-transform group-hover:translate-x-1" data-id="element-196" />
            </a>
          </motion.div>
        </div>
      </div>
    </section>;
}